// Off-screen fixed-pixel share cards rendered for PNG export via html-to-image.
// Two formats:
//   - "social" 1200×630 (OG/Twitter/Facebook/LinkedIn) — absolute-positioned so
//     tall persona titles never push the stats/watermark off-canvas.
//   - "story"  1080×1920 (IG/TikTok/Snap stories) — flow layout.
// All styling is inline pixel-based so the exported PNG is exact regardless of
// zoom, viewport, or tailwind context.
import { forwardRef } from "react";

const FONT_DISPLAY = "'Bebas Neue', Impact, sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, monospace";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const RED = "#FF3B30";
const BG = "#050505";
const LOGO_SRC = "/brand/otaku-index-logo-dark.webp";

function topGenreEntries(breakdown, limit) {
  const entries = Object.entries(breakdown || {});
  const total = entries.reduce((sum, [, c]) => sum + c, 0);
  return entries
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([name, count]) => ({
      name,
      count,
      pct: total ? (count / total) * 100 : 0,
    }));
}

function buildStats(data) {
  return [
    { label: "Anime", value: data.total_anime_in_catalogue ?? "—" },
    { label: "Hours", value: data.total_watch_time_hours != null ? Math.round(data.total_watch_time_hours) : "—" },
    { label: "Top Genre", value: data.top_genre || "—", accent: true },
    { label: "Avg Rating", value: data.average_rating_given ?? "—" },
  ];
}

// Auto-size the persona title so long names fit on one line.
// Bebas-Neue-uppercase with letter-spacing -0.02em ≈ 0.4 × fontSize per char.
function fitTitleSize(title, maxSize, minSize, widthPx) {
  const CHAR_RATIO = 0.42;
  const chars = Math.max(1, (title || "").length);
  const fitted = Math.floor(widthPx / (chars * CHAR_RATIO));
  return Math.max(minSize, Math.min(maxSize, fitted));
}

const monoLabel = (size) => ({
  fontFamily: FONT_MONO,
  fontSize: `${size}px`,
  letterSpacing: "0.35em",
  textTransform: "uppercase",
  color: "#888",
  margin: 0,
});

// ---------------------------------------------------------------------------
// SOCIAL — 1200×630, absolute positioning so overflow is impossible
// ---------------------------------------------------------------------------
function SocialCard({ data, innerRef }) {
  const W = 1200;
  const H = 630;
  const PAD = 48;
  const GENRES_COL_WIDTH = 380;
  const COL_GAP = 48;
  const genres = topGenreEntries(data.genre_breakdown, 4);
  const stats = buildStats(data);
  const titleBlockWidth = W - PAD * 2 - GENRES_COL_WIDTH - COL_GAP;
  const titleSize = fitTitleSize(data.personality_type, 60, 40, titleBlockWidth);

  return (
    <div
      ref={innerRef}
      data-testid="persona-card-social"
      style={{
        width: `${W}px`,
        height: `${H}px`,
        backgroundColor: BG,
        color: "#fff",
        fontFamily: FONT_BODY,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Radial red glow */}
      <div
        style={{
          position: "absolute",
          top: "-50%",
          right: "-25%",
          width: "900px",
          height: "900px",
          background: `radial-gradient(circle at center, ${RED}22 0%, transparent 60%)`,
          pointerEvents: "none",
        }}
      />
      {/* Corner tick */}
      <div
        style={{
          position: "absolute",
          top: `${PAD - 8}px`,
          right: `${PAD - 8}px`,
          width: "24px",
          height: "24px",
          borderTop: `2px solid ${RED}`,
          borderRight: `2px solid ${RED}`,
          pointerEvents: "none",
        }}
      />

      {/* Header (top strip) */}
      <div
        style={{
          position: "absolute",
          top: `${PAD}px`,
          left: `${PAD}px`,
          right: `${PAD}px`,
          height: "42px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,255,255,0.12)",
          paddingBottom: "10px",
          boxSizing: "content-box",
        }}
      >
        <img
          src={LOGO_SRC}
          alt="Otaku Index"
          crossOrigin="anonymous"
          style={{ height: "36px", width: "auto", objectFit: "contain" }}
        />
        <span style={monoLabel(11)}>Persona Report</span>
      </div>

      {/* Persona title + description */}
      <div style={{ position: "absolute", top: `${PAD + 70}px`, left: `${PAD}px`, width: `${titleBlockWidth}px` }}>
        <p style={monoLabel(11)}>You are</p>
        <h1
          style={{
            fontFamily: FONT_DISPLAY,
            fontSize: `${titleSize}px`,
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
            textTransform: "uppercase",
            color: "#fff",
            margin: "8px 0 0 0",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {data.personality_type}
        </h1>
        <p
          style={{
            fontSize: "17px",
            lineHeight: 1.4,
            color: "#c8c8c8",
            marginTop: "22px",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {data.core_description}
        </p>
      </div>

      {/* Top genres — right column */}
      <div
        style={{
          position: "absolute",
          top: `${PAD + 70}px`,
          right: `${PAD}px`,
          width: "380px",
        }}
      >
        <p style={{ ...monoLabel(10), marginBottom: "10px" }}>Top Genres</p>
        {genres.map((g, i) => (
          <div key={g.name} style={{ marginBottom: "8px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "3px" }}>
              <span
                style={{
                  fontFamily: FONT_DISPLAY,
                  fontSize: `${i === 0 ? 20 : 16}px`,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: i === 0 ? RED : "#fff",
                }}
              >
                {g.name}
              </span>
              <span
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: "11px",
                  letterSpacing: "0.15em",
                  color: "#888",
                }}
              >
                {g.count} · {g.pct.toFixed(0)}%
              </span>
            </div>
            <div style={{ height: "4px", width: "100%", backgroundColor: "rgba(255,255,255,0.08)" }}>
              <div
                style={{
                  height: "100%",
                  width: `${Math.max(g.pct, 4)}%`,
                  backgroundColor: i === 0 ? RED : "#fff",
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Stats grid — bottom row */}
      <div
        style={{
          position: "absolute",
          left: `${PAD}px`,
          right: `${PAD}px`,
          bottom: `${PAD + 44}px`,
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "16px",
        }}
      >
        {stats.map((s) => (
          <div
            key={s.label}
            style={{
              borderLeft: `2px solid ${s.accent ? RED : "rgba(255,255,255,0.25)"}`,
              paddingLeft: "14px",
              minWidth: 0,
            }}
          >
            <p style={{ ...monoLabel(10), color: "#888" }}>{s.label}</p>
            <p
              style={{
                fontFamily: FONT_DISPLAY,
                fontSize: "36px",
                lineHeight: 1,
                letterSpacing: "-0.01em",
                textTransform: "uppercase",
                color: "#fff",
                margin: "6px 0 0 0",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {s.value}
            </p>
          </div>
        ))}
      </div>

      {/* Watermark footer */}
      <div
        style={{
          position: "absolute",
          left: `${PAD}px`,
          right: `${PAD}px`,
          bottom: `${PAD - 8}px`,
          paddingTop: "12px",
          borderTop: "1px solid rgba(255,255,255,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: FONT_MONO,
          fontSize: "12px",
          letterSpacing: "0.3em",
          textTransform: "uppercase",
          color: "#888",
        }}
      >
        <span style={{ color: "#fff" }}>otakuindex.app</span>
        <span>Track · Analyze · Share</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// STORY — 1080×1920, vertical flow
// ---------------------------------------------------------------------------
function StoryCard({ data, innerRef }) {
  const W = 1080;
  const H = 1920;
  const PAD = 80;
  const genres = topGenreEntries(data.genre_breakdown, 5);
  const stats = buildStats(data);
  const titleSize = fitTitleSize(data.personality_type, 140, 90, W - PAD * 2);

  return (
    <div
      ref={innerRef}
      data-testid="persona-card-story"
      style={{
        width: `${W}px`,
        height: `${H}px`,
        backgroundColor: BG,
        color: "#fff",
        fontFamily: FONT_BODY,
        padding: `${PAD}px`,
        boxSizing: "border-box",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Radial red glow */}
      <div
        style={{
          position: "absolute",
          top: "-15%",
          right: "-30%",
          width: "1200px",
          height: "1200px",
          background: `radial-gradient(circle at center, ${RED}22 0%, transparent 60%)`,
          pointerEvents: "none",
        }}
      />
      {/* Corner tick */}
      <div
        style={{
          position: "absolute",
          top: `${PAD - 12}px`,
          right: `${PAD - 12}px`,
          width: "34px",
          height: "34px",
          borderTop: `3px solid ${RED}`,
          borderRight: `3px solid ${RED}`,
          pointerEvents: "none",
        }}
      />

      {/* Header */}
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,255,255,0.12)",
          paddingBottom: "24px",
        }}
      >
        <img
          src={LOGO_SRC}
          alt="Otaku Index"
          crossOrigin="anonymous"
          style={{ height: "60px", width: "auto", objectFit: "contain" }}
        />
        <span style={monoLabel(18)}>Persona Report</span>
      </div>

      {/* Persona title */}
      <div style={{ position: "relative", marginTop: "80px" }}>
        <p style={monoLabel(18)}>You are</p>
        <h1
          style={{
            fontFamily: FONT_DISPLAY,
            fontSize: `${titleSize}px`,
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
            textTransform: "uppercase",
            color: "#fff",
            margin: "24px 0 0 0",
            padding: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {data.personality_type}
        </h1>
        <p
          style={{
            fontSize: "30px",
            lineHeight: 1.4,
            color: "#c8c8c8",
            marginTop: "36px",
            display: "-webkit-box",
            WebkitLineClamp: 5,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {data.core_description}
        </p>
      </div>

      {/* Genres */}
      <div style={{ position: "relative", marginTop: "72px" }}>
        <p style={{ ...monoLabel(18), marginBottom: "28px" }}>Top Genres</p>
        {genres.map((g, i) => (
          <div key={g.name} style={{ marginBottom: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "8px" }}>
              <span
                style={{
                  fontFamily: FONT_DISPLAY,
                  fontSize: `${i === 0 ? 42 : 32}px`,
                  letterSpacing: "0.03em",
                  textTransform: "uppercase",
                  color: i === 0 ? RED : "#fff",
                  whiteSpace: "nowrap",
                }}
              >
                {g.name}
              </span>
              <span
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: "22px",
                  letterSpacing: "0.15em",
                  color: "#888",
                }}
              >
                {g.count} · {g.pct.toFixed(0)}%
              </span>
            </div>
            <div style={{ height: "8px", width: "100%", backgroundColor: "rgba(255,255,255,0.08)" }}>
              <div
                style={{
                  height: "100%",
                  width: `${Math.max(g.pct, 4)}%`,
                  backgroundColor: i === 0 ? RED : "#fff",
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Stats — 2×2 grid */}
      <div
        style={{
          position: "relative",
          marginTop: "60px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "28px",
        }}
      >
        {stats.map((s) => (
          <div
            key={s.label}
            style={{
              borderLeft: `3px solid ${s.accent ? RED : "rgba(255,255,255,0.25)"}`,
              paddingLeft: "24px",
              minWidth: 0,
            }}
          >
            <p style={{ ...monoLabel(18), color: "#888" }}>{s.label}</p>
            <p
              style={{
                fontFamily: FONT_DISPLAY,
                fontSize: "80px",
                lineHeight: 1,
                letterSpacing: "-0.01em",
                textTransform: "uppercase",
                color: "#fff",
                margin: "14px 0 0 0",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {s.value}
            </p>
          </div>
        ))}
      </div>

      {/* Footer watermark — pinned to bottom */}
      <div
        style={{
          position: "relative",
          marginTop: "auto",
          paddingTop: "24px",
          borderTop: "1px solid rgba(255,255,255,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: FONT_MONO,
          fontSize: "22px",
          letterSpacing: "0.3em",
          textTransform: "uppercase",
          color: "#888",
        }}
      >
        <span style={{ color: "#fff" }}>otakuindex.app</span>
        <span>Track · Analyze · Share</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public component — dispatches to the right format
// ---------------------------------------------------------------------------
const PersonaShareCard = forwardRef(function PersonaShareCard({ data, format = "social" }, ref) {
  if (format === "story") return <StoryCard data={data} innerRef={ref} />;
  return <SocialCard data={data} innerRef={ref} />;
});

export default PersonaShareCard;
